import React, { useEffect, useState, useCallback, useRef } from 'react';

import {
  Button,
  Checkbox,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';

import AttachFileIcon from '@mui/icons-material/AttachFile';
import LibraryAddCheckOutlined from '@mui/icons-material/LibraryAddCheckOutlined';

import { useBookingModalStyles } from './styles';
import { useBookingsContext } from '../../contexts/BookingsContext';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

import { parse, format } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import useBookings from '../../hooks/useBookings';
import { IPlan } from '../../global/interfaces';
import { IBookingAttachment, IBooking } from '@homeporter/types';
import { timezones } from '../../global/constants/general';
import ViewAttachments from '../ViewAttachments';
import ToggleButton from '../ToggleButton';
import { useCopilotContext } from '../../contexts/CopilotContext';

const SpotBooking = ({
  handleCloseBookingModal,
}: {
  handleCloseBookingModal: (redirect?: string | undefined) => void;
}) => {
  const styles = useBookingModalStyles();

  const { getBookingDetails } = useBookings();
  const {
    description: selectedBookingDescription,
    editBooking,
    setEditBooking,
    setBookingModalUrl,
    selectedBooking,
    setSelectedPlan,
    setUserInfo,
  } = useBookingsContext();

  const { formList } = useCopilotContext();

  const [bookingId, setBookingId] = useState<number>(0);
  const [bookingTitle, setBookingTitle] = useState<string>('');
  const [timezoneIana, setTimeZoneIana] = useState<string>(timezones[3]);
  const [sessionDate, setSessionDate] = useState<Date>(new Date());
  const [description, setDescription] = useState<string>('');

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<IBookingAttachment[]>([]);
  const [selectedFileIndex, setSelectedFileIndex] = useState(-1);

  const [taskList, setTaskList] = useState<Record<string, boolean>>({});
  const [showTasks, setShowTasks] = useState<boolean>(false);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (selectedBooking && selectedBooking === editBooking) return;

    const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const matchingTimezone = timezones.find((zone) => zone === currentTimezone);

    if (matchingTimezone) {
      setTimeZoneIana(matchingTimezone);
    }

    if (editBooking) {
      setBookingId(editBooking?.booking_id || 0);
      setBookingTitle(editBooking?.title || '');
      setTimeZoneIana(editBooking?.timezone_name || timezones[3]);
      setSessionDate(new Date(editBooking?.booking_date || new Date()));
      setDescription(editBooking?.description || '');
      setTaskList(editBooking?.tasks || {});
      setShowTasks(Object.keys(editBooking?.tasks || {}).length > 0);
      setSelectedFiles(editBooking?.attachments || []);
      return;
    }

    if (selectedBooking) {
      const getBooking = async (editBooking: IBooking) => {
        if (!selectedBooking?.booking_id) return;

        try {
          const resp = await getBookingDetails(selectedBooking.booking_id);

          resp?.userInfo &&
            setUserInfo({
              ...resp?.userInfo,
            });
        } catch (error) {
          console.error('Error fetching booking:', error);
          alert('Error fetching booking details!');
        }
      };

      setBookingId(selectedBooking?.booking_id || 0);
      setBookingTitle(selectedBooking?.title || '');
      setTimeZoneIana(selectedBooking?.timezone_name || timezones[3]);
      setSessionDate(new Date(selectedBooking?.booking_date || new Date()));
      setDescription(selectedBookingDescription || '');
      setTaskList(selectedBooking?.tasks || {});
      setSelectedFiles(selectedBooking?.attachments || []);
      setShowTasks(Object.keys(selectedBooking?.tasks || {}).length > 0);

      const plan = selectedBooking.payment.plan;
      setSelectedPlan({ ...plan, optional_address: true });

      const editBooking = JSON.parse(JSON.stringify(selectedBooking));
      setEditBooking(editBooking);
      getBooking(editBooking);
      return;
    }

    setEditBooking({
      payment_id: 0,
      resource_id: '',
      confirmation_code: '',
      session_duration: 0,
      inspector_id: 0,
      title: '',
      description: '',
      mobile_number: '',
      mobile_status: '',
      attachments: [],
      tasks: {},
      payment: {
        reference: '',
        plan: {
          label: '',
        },
        user: {
          firstname: '',
          lastname: '',
          email: '',
        },
      },
      mobile_details: '',
      booking_session_code: '',
      guest_entry_code: '',
      authorization_code: '',
      address: '',
      city: '',
      province: '',
      postal_code: '',
      country_code: '',
      status: 0,
      booking_date: '',
      created_at: '',
      updated_at: '',
    });

    setSelectedPlan({
      plan_id: 0,
      notification_schedule: {},
      label: 'Spot Session',
      name: 'spot session',
      optional_address: true,
    } as IPlan);

    console.log('::::initSpotBooking...');
  }, [selectedBooking]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  const convertFileList = (
    prev: IBookingAttachment[],
    files: File[],
  ): IBookingAttachment[] => {
    const convertedFiles = files.map((file) => ({
      id: Math.random(), // Temporary unique id
      name: file.name,
      type: file.type,
      size: file.size,
      url: '', //URL.createObjectURL(file),
      last_modified: file.lastModified,
      uploaded_at: new Date().toISOString(),
      file: file,
    }));

    const combined = [...prev, ...convertedFiles];
    const uniqueAttachments = combined.filter(
      (attachment, index, self) =>
        index === self.findIndex((a) => a.name === attachment.name),
    );

    return uniqueAttachments;
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const files = Array.from(e.dataTransfer.files || []);
      setSelectedFiles((prev: IBookingAttachment[]) =>
        convertFileList(prev, files),
      );
    }
  }, []);

  const handleContinue = () => {
    if (!editBooking || bookingTitle.length === 0) return;

    const wallTime = format(sessionDate, 'yyyy-MM-dd HH:mm:ss');
    const parsed = parse(wallTime, 'yyyy-MM-dd HH:mm:ss', new Date());
    const dateInUtc = fromZonedTime(parsed, timezoneIana);

    editBooking.title = bookingTitle;
    editBooking.timezone_name = timezoneIana;
    editBooking.booking_date = formatInTimeZone(
      dateInUtc,
      timezoneIana,
      'yyyy-MM-dd HH:mm:ssXXX',
    );
    editBooking.description = description;
    editBooking.tasks = { ...taskList };
    editBooking.attachments = [...selectedFiles];

    setBookingModalUrl(`booking-details`);
  };

  const attachmentTitle = () => {
    return (
      <>
        <Typography> Attachments</Typography>
        <IconButton
          onClick={() => fileInputRef.current?.click()}
          sx={styles.attachmentsIcon}>
          <AttachFileIcon />
        </IconButton>
      </>
    );
  };

  const setTask = (
    tasks: Record<string, boolean>,
    code: string,
    value: boolean,
  ) => {
    return Object.fromEntries(
      Object.entries({ ...tasks, [code]: value }).filter(
        ([_, v]) => v !== false,
      ),
    );
  };

  return (
    <>
      {selectedFiles.length > 0 && (
        <ViewAttachments
          attachments={selectedFiles}
          selectedAttachmentIndex={selectedFileIndex}
          setSelectedAttachmentIndex={setSelectedFileIndex}
        />
      )}

      <Box
        id="booking-spot-container"
        sx={styles.container}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}>
        <Typography style={styles.title}>
          <Button
            sx={{
              ...styles.button,
              ...styles.close,
            }}
            onClick={() => handleCloseBookingModal()}>
            Close
          </Button>
          {bookingId ? 'Edit Appointment' : 'Enter Appointment Details'}
        </Typography>

        {/*<Box sx={{ minHeight: '20rem', width: '100%', height: '100%' }}>*/}
        {dragActive && (
          <Box sx={styles.dropzone}>
            <Typography variant="h6" color="textSecondary">
              Drop work objects here
            </Typography>
          </Box>
        )}

        <Grid container direction="column" sx={styles.gridButton}>
          <Grid item sx={{ height: '4.5rem', pr: '1rem' }}>
            <TextField
              required
              label="Title / Roll Number"
              variant="outlined"
              name="bookingtitle"
              value={bookingTitle}
              onChange={(e) => setBookingTitle(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item sx={styles.gridDate}>
            <Box sx={{ flex: 1 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Session Date & Time"
                  value={sessionDate}
                  onChange={(newValue: any) =>
                    newValue && setSessionDate(newValue)
                  }
                  ampm={false}
                  minutesStep={5}
                  sx={{ width: '100%' }}
                />
              </LocalizationProvider>
            </Box>
            <Box sx={{ flex: 1 }}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel id="timezone-label">Timezone</InputLabel>
                <Select
                  labelId="timezone-label"
                  label="Timezone"
                  value={timezoneIana}
                  onChange={(e) => setTimeZoneIana(e.target.value)}>
                  {timezones.map((zone) => (
                    <MenuItem key={zone} value={zone}>
                      {zone}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>

          <Grid item sx={styles.extra}>
            <Box sx={styles.attachments}>
              <IconButton
                sx={styles.attachmentsIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('::::showActions', showTasks);
                  setShowTasks(!showTasks);
                }}>
                <LibraryAddCheckOutlined />
              </IconButton>
              <Box sx={styles.taskList}>
                <Typography sx={{ mr: 'auto' }}>Select Work Objects</Typography>

                {!showTasks &&
                  formList.map(({ code, title }, index) => (
                    <ToggleButton
                      key={index}
                      label={title}
                      checked={taskList[code]}
                      onChange={(value) => {
                        setTaskList(setTask(taskList, code, value));
                      }}
                      styles={{ marginRight: 0, marginLeft: '.5rem' }}
                      icon={<Typography fontSize={'.75rem'}>{code}</Typography>}
                    />
                  ))}
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                id="file-upload"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setSelectedFiles((prev) => convertFileList(prev, files));
                }}
                multiple
              />
              {!showTasks && attachmentTitle()}
            </Box>

            {showTasks && (
              <Box sx={styles.checkboxlist}>
                {formList.map(({ code, title }, index) => (
                  <Box key={index} sx={styles.checkboxitem}>
                    <Checkbox
                      checked={taskList[code]}
                      onChange={(e) => {
                        setTaskList(setTask(taskList, code, e.target.checked));
                      }}
                      sx={styles.checkbox}
                    />
                    <Typography noWrap>
                      [{code}] - {title}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {showTasks && (
              <Box sx={styles.attachments}>{attachmentTitle()}</Box>
            )}
            {selectedFiles.length > 0 && (
              <Box sx={styles.filelist}>
                {selectedFiles.map((file, index) => (
                  <Box key={index} sx={styles.file}>
                    <Typography
                      noWrap
                      sx={styles.filename}
                      title={file.name}
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log('::::index: ', index);
                        setSelectedFileIndex(index);
                      }}>
                      {file.name}
                    </Typography>
                    <IconButton
                      onClick={() => {
                        setSelectedFiles((files) =>
                          files.filter((_, i) => i !== index),
                        );
                      }}
                      sx={styles.delete}>
                      <DeleteOutline sx={{ fontSize: '1.2rem' }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Grid>

          <Grid item sx={{ ...styles.gridDescription, paddingTop: '1rem' }}>
            <TextField
              label="Description"
              variant="outlined"
              name="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={4}
              InputLabelProps={{
                shrink: true,
              }}
              sx={styles.gridDescription2}
            />
          </Grid>
        </Grid>

        {/*</Box>*/}
        <Box sx={styles.buttonContainer} id="booking-spot-button-container">
          <Button
            sx={{
              ...styles.button,
              ...styles.buttonPrimary,
              ...(bookingTitle.length === 0 && { opacity: '50%' }),
            }}
            onClick={handleContinue}>
            Continue
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default SpotBooking;
